import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { KeyScope } from './keys';

/**
 * Binding a screen's keys and declaring them are one act.
 *
 * `keys.ts` says a key listed and not bound is a lie, and the app told one on every full task
 * view: `?` there printed every key the task list binds — j, k, l, h, Space, Shift-j, a, x, 1 2 3
 * … — and pressed none of them, because the list that binds them is unmounted while the read view
 * has the pane. Nothing could have caught it. `App.tsx` computed the panel's scope from its own
 * `screen` state (and, for list-versus-board, from a `localStorage` read during render), which is
 * a *second* claim about where you are, made by a file that binds no keys at all. Two claims, so
 * one of them was wrong.
 *
 * There is one claim now. `useKeys(scope, handler)` installs the handler **and** publishes the
 * scope for exactly as long as the handler is installed, so the panel cannot show a group whose
 * screen is not listening, and a screen cannot listen without saying which group it is. A screen
 * that binds nothing gets `Anywhere` and nothing else — which is what the read view had before it
 * grew keys of its own, and the right answer for the next screen somebody adds.
 *
 * What this does *not* mechanise: whether a group lists exactly the keys its handler tests. The
 * handlers are one branchy function each (`Shift-j` and `j` are the same branch tree; `h`/`l`
 * share a body; `1 2 3` come off a table) and a per-key handler map would have to rewrite that
 * logic to prove something a reader can already see beside it. What is mechanised is the pairing
 * — see `keyScopesAreClaimed.spec.ts`, which fails on a group no screen claims, on a screen that
 * claims no group, and on a screen that hand-writes a `<kbd>` instead of rendering `inlineKeys`.
 *
 * A module-level registry rather than a context, because these listeners are on `document`
 * already: there is one keyboard, and a provider would only be a second way to reach the same
 * singleton.
 */

type Scope = Exclude<KeyScope, null>;

/** The scopes listening right now, oldest first. Normally one; a stack only while React swaps. */
let claimed: readonly Scope[] = [];
const watchers = new Set<() => void>();

function claim(scope: Scope): () => void {
  claimed = [...claimed, scope];
  for (const w of watchers) w();
  return () => {
    /*
      By identity of position, not by value: two screens could in principle claim the same scope,
      and dropping every match would unpublish a live one. `indexOf` takes the oldest, which is
      the one this release belongs to under React's mount-before-unmount ordering.
    */
    const at = claimed.indexOf(scope);
    if (at === -1) return;
    claimed = [...claimed.slice(0, at), ...claimed.slice(at + 1)];
    for (const w of watchers) w();
  };
}

function subscribe(watcher: () => void): () => void {
  watchers.add(watcher);
  return () => watchers.delete(watcher);
}

/** The scope whose keys are live, or `undefined` when the screen on show binds none. */
export function useBoundScope(): Scope | undefined {
  return useSyncExternalStore(
    subscribe,
    /*
      The newest claim wins. React mounts the incoming screen's effects before it unmounts the
      outgoing one's, so during a swap both are briefly published and the arriving screen is the
      honest answer — and it is also the one whose listener runs last, which is the one a key press
      would reach.
    */
    () => claimed[claimed.length - 1],
    () => undefined,
  );
}

/**
 * Bind this screen's keyboard, for as long as it is on screen.
 *
 * Pass `null` for a screen that is mounted but not listening — the Tasks screen renders the board
 * inside the list, and only one of the two may own the keyboard at a time.
 *
 * The handler is read through a ref, so a caller passes a fresh closure every render and needs no
 * dependency array. That also means the listener registers **once**, on mount, rather than being
 * torn down and re-added on every data change: the list's Escape layering depends on its handler
 * being the one that registered first (see `hasOpenSurface`), and re-registration kept quietly
 * moving it to the back of the queue.
 */
export function useKeys(scope: Scope, handler: ((e: KeyboardEvent) => void) | null): void {
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });

  const listening = handler !== null;
  useEffect(() => {
    if (!listening) return;
    const onKey = (e: KeyboardEvent) => latest.current?.(e);
    document.addEventListener('keydown', onKey);
    const release = claim(scope);
    return () => {
      document.removeEventListener('keydown', onKey);
      release();
    };
  }, [scope, listening]);
}
